import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Terminal,
  FileText,
  Pencil,
  Search,
  Globe,
  FolderOpen,
  ChevronRight,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { useFilePreview } from "../../lib/filePreview";

interface Action {
  name: string;
  args?: Record<string, unknown>;
  result?: string;
  pre?: string;
  post?: string;
}

const ICONS: Record<string, typeof Terminal> = {
  run_terminal: Terminal,
  read_file: FileText,
  list_files: FolderOpen,
  write_file: Pencil,
  edit_file: Pencil,
  create_file: Pencil,
  search_code: Search,
  web_search: Globe,
  web_fetch: Globe,
};

const LABELS: Record<string, string> = {
  run_terminal: "Ran",
  read_file: "Read",
  list_files: "Listed",
  write_file: "Wrote",
  edit_file: "Edited",
  create_file: "Created",
  search_code: "Searched",
  web_search: "Searched web",
  web_fetch: "Fetched",
};

function getIcon(name: string) {
  const lower = name.toLowerCase();
  for (const [key, Icon] of Object.entries(ICONS)) {
    if (lower.includes(key)) return Icon;
  }
  if (lower.includes("read")) return FileText;
  if (lower.includes("write") || lower.includes("edit") || lower.includes("create")) return Pencil;
  if (lower.includes("terminal") || lower.includes("run") || lower.includes("exec")) return Terminal;
  if (lower.includes("search")) return Search;
  return Terminal;
}

function getLabel(name: string) {
  const lower = name.toLowerCase();
  for (const [key, label] of Object.entries(LABELS)) {
    if (lower.includes(key)) return label;
  }
  return name;
}

function getDetail(args?: Record<string, unknown>): string {
  if (!args) return "";
  const val = args.command ?? args.path ?? args.file ?? args.query ?? args.url ?? args.pattern;
  if (typeof val === "string") {
    const s = val.trim();
    return s.length > 80 ? s.slice(0, 77) + "…" : s;
  }
  return "";
}

function ActionLine({ action, streaming }: { action: Action; streaming: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const openFile = useFilePreview((s) => s.open);
  const Icon = getIcon(action.name);
  const label = getLabel(action.name);
  const detail = getDetail(action.args);
  const done = action.result !== undefined;
  const running = !done && streaming;

  // Is this a file operation with a path?
  const filePath = (action.args?.path ?? action.args?.file) as string | undefined;
  const isFileOp = filePath && /write|read|edit|create/i.test(action.name);

  function handleClick() {
    if (isFileOp && done) {
      openFile(filePath!);
    } else if (done) {
      setExpanded(!expanded);
    }
  }

  return (
    <div className="border-l-2 border-border/60 pl-3 py-0.5">
      <button
        type="button"
        onClick={handleClick}
        className="flex items-center gap-2 w-full text-left text-small hover:bg-bg-subtle/40 rounded px-1 py-0.5 transition-colors"
        disabled={!done}
      >
        {running ? (
          <Loader2 className="size-3.5 text-accent-400 animate-spin flex-shrink-0" />
        ) : (
          <Icon className="size-3.5 text-fg-muted flex-shrink-0" />
        )}
        <span className="text-fg-muted">{label}</span>
        {detail && (
          <span className="font-mono text-fg-subtle text-[0.75rem] truncate max-w-[350px]">
            {detail}
          </span>
        )}
        {done && (
          <CheckCircle2 className="size-3 text-success flex-shrink-0 ml-auto" />
        )}
        {done && action.result && (
          <ChevronRight
            className={`size-3 text-fg-subtle transition-transform ${expanded ? "rotate-90" : ""}`}
          />
        )}
      </button>
      <AnimatePresence>
        {expanded && action.result && (
          <motion.pre
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.12 }}
            className="ml-6 mt-1 text-[0.72rem] text-fg-subtle font-mono bg-bg-subtle/50 rounded px-2 py-1 overflow-x-auto max-h-[120px] overflow-y-auto whitespace-pre-wrap"
          >
            {action.result.slice(0, 500)}
          </motion.pre>
        )}
      </AnimatePresence>
    </div>
  );
}

export function ActionLog({ actions, streaming }: { actions: Action[]; streaming: boolean }) {
  if (!actions || actions.length === 0) return null;
  return (
    <div className="space-y-0.5 my-2">
      {actions.map((a, i) => (
        <ActionLine key={i} action={a} streaming={streaming && i === actions.length - 1} />
      ))}
    </div>
  );
}
